import { useState, type KeyboardEvent } from 'react';
import { Plus, X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface LabelChipsProps {
  labels: readonly string[];
  /** Labels to flag (e.g. not lower_snake_case, duplicates). */
  invalid?: ReadonlySet<string>;
  /** One label shown dashed, with a hint, e.g. the fallback label. */
  highlighted?: string | null;
  highlightedHint?: string;
  disabled?: boolean;
  normalize?: (text: string) => string;
  onChange: (labels: string[]) => void;
  /** Placeholder for the "add" box. */
  placeholder?: string;
  testIdPrefix?: string;
}

const trimLabel = (text: string) => text.trim();

/**
 * A set of plain string labels as chips: click a chip to rename it, the ×
 * removes it, the box at the end adds one (Enter or comma commits). Used by
 * Classify / Decide; the Jev panel has its own chip because its labels carry
 * criteria.
 */
export function LabelChips({
  labels,
  invalid,
  highlighted = null,
  highlightedHint = 'fallback only',
  disabled = false,
  normalize = trimLabel,
  onChange,
  placeholder = 'add a label…',
  testIdPrefix = 'label',
}: LabelChipsProps) {
  const [draft, setDraft] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const commitAdd = () => {
    const next = normalize(draft);
    setDraft('');
    if (!next || labels.includes(next)) return;
    onChange([...labels, next]);
  };

  const commitEdit = (index: number) => {
    const next = normalize(editDraft);
    setEditingIndex(null);
    if (!next) {
      onChange(labels.filter((_, i) => i !== index));
      return;
    }
    if (next === labels[index]) return;
    if (labels.includes(next)) return;
    onChange(labels.map((label, i) => (i === index ? next : label)));
  };

  const onAddKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitAdd();
    }
  };

  return (
    <div
      className='flex flex-wrap items-center gap-1.5'
      data-testid={`${testIdPrefix}-chips`}
    >
      {labels.map((label, index) =>
        editingIndex === index && !disabled ? (
          <Input
            key={`edit-${index}`}
            autoFocus
            value={editDraft}
            aria-label={`Rename label ${label}`}
            data-testid={`${testIdPrefix}-chip-edit`}
            className='h-7 w-36 text-xs font-mono'
            onChange={(event) => setEditDraft(event.target.value)}
            onBlur={() => commitEdit(index)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitEdit(index);
              }
              if (event.key === 'Escape') setEditingIndex(null);
            }}
          />
        ) : (
          <span
            key={`${label}-${index}`}
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full border py-0.5 pl-0.5 pr-1 text-xs',
              invalid?.has(label)
                ? 'border-red-400 bg-red-50 dark:bg-red-950/30'
                : highlighted === label
                  ? 'border-dashed border-amber-400 bg-amber-50 dark:bg-amber-950/20'
                  : 'border-border bg-muted/40',
              !disabled && 'hover:border-blue-400',
            )}
            data-testid={`${testIdPrefix}-chip-${label}`}
          >
            <button
              type='button'
              disabled={disabled}
              aria-label={`Edit label ${label}`}
              className='rounded-full px-2 py-0.5 font-mono font-semibold disabled:cursor-default'
              onClick={() => {
                setEditingIndex(index);
                setEditDraft(label);
              }}
            >
              {label}
            </button>
            {highlighted === label ? (
              <span className='pr-1 text-[10px] text-amber-700 dark:text-amber-300'>
                {highlightedHint}
              </span>
            ) : null}
            {!disabled ? (
              <button
                type='button'
                aria-label={`Remove label ${label}`}
                data-testid={`${testIdPrefix}-remove-${label}`}
                className='rounded-full p-0.5 text-muted-foreground hover:bg-background hover:text-foreground'
                onClick={() => onChange(labels.filter((_, i) => i !== index))}
              >
                <X className='size-3' />
              </button>
            ) : null}
          </span>
        ),
      )}
      {!disabled ? (
        <span className='inline-flex items-center gap-1'>
          <Plus className='size-3 text-muted-foreground' />
          <Input
            value={draft}
            placeholder={placeholder}
            aria-label='Add a label'
            data-testid={`${testIdPrefix}-add`}
            className='h-7 w-36 text-xs font-mono'
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onAddKey}
            onBlur={() => draft.trim() && commitAdd()}
          />
        </span>
      ) : null}
    </div>
  );
}
